/* eslint-disable no-param-reassign */

/* 
  TODO: 
  This was copied from marketplace. It may end up in zola-ui/zola-helpers/wherever 

  When it does, we'll have to delete this and use the common implementation
*/

import { RefObject, useEffect, useRef, useState } from 'react';
import useResizeObserver from '@react-hook/resize-observer';
import _sum from 'lodash/sum';

/**
 * @returns The children of a target element as a list
 */
const getChildren = (target: Element | null) => {
  const total: HTMLElement[] = [];
  target?.childNodes.forEach(item => {
    if (!(item instanceof HTMLElement)) return;
    total.push(item);
  });
  return total;
};

/**
 * @returns The gap between each child element
 */
const getElementGap = (target: Element | null) => {
  if (!target) return 0;

  const containerStyle = window.getComputedStyle(target);
  return parseInt(containerStyle.getPropertyValue('gap'), 10) || 0;
};

/**
 * Directly mutate an element to make it not visible to the user,
 * but still take up width in the dom so it can be measured
 */
function setElementHidden(element: HTMLElement) {
  element.style.visibility = 'hidden';
  element.style.left = '-1000px';
  element.style.position = 'absolute';
}

/**
 * Makes an element visible
 */
function setElementVisible(element: HTMLElement) {
  element.style.position = 'initial';
  element.style.visibility = 'visible';
}

/**
 * Returns the total with of a list of elements
 */
function getElementListWidth(allChildren: HTMLElement[], gap: number) {
  const totalChildrenWidth = _sum(allChildren.map(element => element.offsetWidth));
  const totalGap = gap * Math.max(allChildren.length - 1, 0);
  return totalChildrenWidth + totalGap;
}

/** Checks if a button has been marked as important */
const isButtonImportant = (button: HTMLElement) => button.dataset.isimportant === 'true';

/**
 * Takes a container and determines if it's children should be visible, if not all
 * children are visible, display a 'more' button.
 */
function updateElementsVisibility(target: Element | null, isCompact: boolean) {
  const allChildren = getChildren(target);
  const gap = getElementGap(target);
  const buttons = allChildren.slice(0, -1);
  const moreButton = allChildren.slice(-1)[0];
  const allButtonsWidth = getElementListWidth(buttons, gap);
  const facetButtonContainerWidth = target?.clientWidth;
  const areAllImportant = buttons.every(isButtonImportant);
  const areAnyImportant = buttons.some(isButtonImportant);

  let spaceAvailableForButtons = facetButtonContainerWidth || 0;

  // TODO: Remove the `isMobile` check for now this is keeping the old mobile behavior
  // if (isMobile()) {
  //   buttons.forEach(setElementVisible);
  //   setElementVisible(moreButton);
  //   return;
  // }

  // If all buttons are important and there is space, show them all and hide the more (or less) button
  if (areAllImportant && allButtonsWidth < spaceAvailableForButtons) {
    buttons.forEach(setElementVisible);
    setElementHidden(moreButton);
    return;
  }

  // If the bar is expanded, show all buttons
  if (!isCompact) {
    buttons.forEach(setElementVisible);
    setElementVisible(moreButton);
    return;
  }

  // Handle the compact version of the bar
  buttons.forEach((button, index) => {
    const isImportant = isButtonImportant(button);
    const isLastElement = index === buttons.length - 1;
    const isPreviousElementHidden = buttons[index - 1]?.style.visibility === 'hidden';
    // If the previous button was hidden, all subsequent buttons should be hidden except the more button
    // Hide buttons that aren't important if the bar is compact
    if (isPreviousElementHidden || (isCompact && !isImportant && areAnyImportant)) {
      setElementHidden(button);
      if (moreButton) setElementVisible(moreButton);
      return;
    }

    if (isLastElement) {
      // If its the last element, check if there is space for just the button since we don't need the more button
      if (spaceAvailableForButtons > button.offsetWidth) {
        setElementVisible(button);
        if (moreButton) setElementHidden(moreButton);
        return;
      }
    }

    // If there's not enough space available for the next button, hide it
    if (spaceAvailableForButtons < button.offsetWidth + (moreButton?.offsetWidth || 0)) {
      setElementHidden(button);
      if (moreButton) setElementVisible(moreButton);
      return;
    }

    // If there is space, show the button
    setElementVisible(button);
    spaceAvailableForButtons -= button.offsetWidth + gap;
    if (isLastElement) {
      // If all the buttons were visible, hide the more button
      if (moreButton) setElementHidden(moreButton);
    }
  });
}

/**
 * Creates an expanding and contracting container element
 */
export function useCollapsingButtons(ref: RefObject<HTMLDivElement>) {
  const [isCompact, setIsCompact] = useState<boolean>(true);

  const widthRef = useRef(0);

  useResizeObserver(ref, ({ target }) => {
    // Don't update if the width is the same and only the height changed
    if (widthRef.current === target.clientWidth) return;
    widthRef.current = target.clientWidth;

    updateElementsVisibility(target, isCompact);
  });

  // Update the visibility before the next render
  useEffect(() => {
    updateElementsVisibility(ref.current, isCompact);
  });

  return [isCompact, setIsCompact] as const;
}
